import React, { useState, useEffect } from 'react';
import { Form, message, ConfigProvider, Select, Button, Space, Popconfirm, Modal } from 'antd';
import zh_TW from 'antd/locale/zh_TW';
import AlarmRuleModule from '../../components/AlarmRuleModule';
import request from "../../api/request";
import { BASE_URL } from "../../api/config";
import './index.scss';

const AlarmSettings = () => {
    const [loading, setLoading] = useState(false);
    const [rules, setRules] = useState([]);
    const [form] = Form.useForm();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState(null);

    // 監測點位置選項
    const locationOptions = [
        { value: '20240930120203', label: '柴頭港溪截流站' },
        { value: '20240930120201', label: 'PA管網' },
        { value: '20240930120202', label: 'PB管網' },
        { value: '20240521120201', label: '廠內進流點' }
    ];

    // 感測器類型選項
    const sensorOptions = {
        '20240930120203': [ // 柴頭港溪截流站
            { value: '12', label: '流量', unit: 'km³/h' },
            { value: '52', label: '導電度', unit: 'mS/cm' },
            { value: '62', label: 'COD', unit: 'mg/L' },
            { value: '63', label: '濁度', unit: 'NTU' },
            { value: '64', label: 'SS', unit: 'mg/L' },
            { value: '65', label: 'pH', unit: '' },
            { value: '66', label: 'ORP', unit: 'mV' }
        ],
        '20240930120201': [ // PA管網
            { value: '50', label: '流量', unit: 'km³/h' },
            { value: '51', label: '導電度', unit: 'mS/cm' }
        ],
        '20240930120202': [ // PB管網
            { value: '50', label: '流量', unit: 'km³/h' },
            { value: '51', label: '導電度', unit: 'mS/cm' }
        ],
        '20240521120201': [ // 廠內進流點
            { value: '50', label: '油脂含量', unit: 'ug/L' }
        ]
    };

    // 警報等級選項
    const rankOptions = [
        { value: 'LL', label: 'LL (極低)' },
        { value: 'L', label: 'L (低)' },
        { value: 'H', label: 'H (高)' },
        { value: 'HH', label: 'HH (極高)' }
    ];

    // 運算符選項
    const operatorOptions = [
        { value: '>', label: '大於' },
        { value: '<', label: '小於' },
        { value: '>=', label: '大於等於' },
        { value: '<=', label: '小於等於' },
        { value: '=', label: '等於' }
    ];

    // 獲取警報規則列表
    const fetchRules = async (locationId = null) => {
        setLoading(true);
        try {
            const params = {
                page_num: 1,
                page_size: 100
            };
            if (locationId) {
                params.log_id = locationId;
            }
            const res = await request.post(BASE_URL + '/water/alarm/rules', params);
            if (res.code === 200) {
                setRules(res.data.rows || []);
            }
        } catch (error) {
            message.error('獲取警報規則失敗');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    // 新增/編輯表單提交
    const handleSubmit = async (values) => {
        try {
            const sensorInfo = sensorOptions[values.log_id]?.find(
                opt => opt.value === values.sensor_id
            );
            
            // 檢查是否至少設置了一個閾值
            if (!values.threshold_ll && !values.threshold_l && 
                !values.threshold_h && !values.threshold_hh) {
                message.error('請至少設置一個警報閾值');
                return;
            }
            
            const ruleData = {
                id: values.id, // 編輯時需要
                log_id: values.log_id,
                sensor_id: values.sensor_id,
                name: `${locationOptions.find(opt => opt.value === values.log_id)?.label} - 
                       ${sensorInfo?.label}警報`,
                threshold_ll: values.threshold_ll ? parseFloat(values.threshold_ll) : null,
                threshold_l: values.threshold_l ? parseFloat(values.threshold_l) : null,
                threshold_h: values.threshold_h ? parseFloat(values.threshold_h) : null,
                threshold_hh: values.threshold_hh ? parseFloat(values.threshold_hh) : null,
                is_active: true,
                message_template: "{sensor_name} {rank} Alarm: {value}"
            };

            const url = values.id 
                ? `${BASE_URL}/water/alarm/rule/update`
                : `${BASE_URL}/water/alarm/rule/add`;
                
            const res = await request.post(url, ruleData);

            if (res.code === 200) {
                message.success(values.id ? '更新成功' : '新增成功');
                fetchRules(values.log_id);
                form.resetFields();
                setIsModalVisible(false);
            } else {
                throw new Error(res.message || (values.id ? '更新失敗' : '新增失敗'));
            }
        } catch (error) {
            message.error(error.message);
            console.error(error);
        }
    };

    // 編輯規則
    const handleEdit = (sensorRules) => {
        // 找到該感測器的所有閾值規則
        const formData = {
            id: sensorRules[0].id,
            log_id: sensorRules[0].log_id,
            sensor_id: sensorRules[0].sensor_id,
            threshold_ll: null,
            threshold_l: null,
            threshold_h: null,
            threshold_hh: null,
            is_active: sensorRules[0].is_active
        };

        // 設置各個閾值
        sensorRules.forEach(rule => {
            const field = `threshold_${rule.rank.toLowerCase()}`;
            formData[field] = rule.set_value;
        });

        form.setFieldsValue(formData);
        setIsModalVisible(true);
    };

    // 刪除規則
    const handleDelete = async (id) => {
        try {
            const res = await request.post(BASE_URL + '/water/alarm/rule/delete', { id });
            if (res.code === 200) {
                message.success('刪除成功');
                fetchRules(form.getFieldValue('log_id'));
            }
        } catch (error) {
            message.error('刪除失敗');
            console.error(error);
        }
    };

    // 監測點位置變更處理
    const handleLocationChange = (value) => {
        form.setFieldValue('sensor_id', undefined);
        fetchRules(value);
    };

    const handleAdd = () => {
        form.resetFields();
        form.setFieldsValue({
            log_id: selectedLocation,
            is_active: true
        });
        setIsModalVisible(true);
    };

    useEffect(() => {
        // 初始化時選擇第一個地點
        const firstLocation = locationOptions[0]?.value;
        if (firstLocation) {
            setSelectedLocation(firstLocation);
            fetchRules(firstLocation);
        }
    }, []);

    return (
        <ConfigProvider locale={zh_TW}>
            <div className="alarm-settings">
                <div className="settings-container">
                    <div className="filter-bar">
                        <div className="filter-items">
                            <span className="label">地點：</span>
                            <Select
                                value={selectedLocation}
                                onChange={(value) => {
                                    setSelectedLocation(value);
                                    fetchRules(value);
                                }}
                                style={{ width: 200 }}
                                options={locationOptions}
                                placeholder="請選擇地點"
                            />
                        </div>
                        <Button type="primary" onClick={handleAdd}>
                            新增警報規則
                        </Button>
                    </div>

                    <div className="rules-table">
                        <div className="table-header">
                            <div className="col sensor">感測器類型</div>
                            <div className="col hh">HH</div>
                            <div className="col h">H</div>
                            <div className="col l">L</div>
                            <div className="col ll">LL</div>
                            <div className="col status">狀態</div>
                            <div className="col actions">操作</div>
                        </div>

                        {/* 根據感測器分組顯示規則 */}
                        {rules.map((rule) => (
                            <div key={`${rule.log_id}-${rule.sensor_id}`} className="table-row">
                                <div className="col sensor">
                                    {sensorOptions[rule.log_id]?.find(opt => 
                                        opt.value === rule.sensor_id
                                    )?.label}
                                </div>
                                <div className="col hh">{rule.threshold_hh || '-'}</div>
                                <div className="col h">{rule.threshold_h || '-'}</div>
                                <div className="col l">{rule.threshold_l || '-'}</div>
                                <div className="col ll">{rule.threshold_ll || '-'}</div>
                                <div className="col status">
                                    <span className={`status-tag ${rule.is_active ? 'enabled' : 'disabled'}`}>
                                        {rule.is_active ? '啟用' : '停用'}
                                    </span>
                                </div>
                                <div className="col actions">
                                    <Space>
                                        <Button 
                                            type="primary" 
                                            size="small" 
                                            onClick={() => handleEdit(rule)}
                                        >
                                            編輯
                                        </Button>
                                        <Popconfirm
                                            title="確定要刪除嗎？"
                                            onConfirm={() => handleDelete(rule.id)}
                                        >
                                            <Button danger size="small">刪除</Button>
                                        </Popconfirm>
                                    </Space>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {isModalVisible && (
                    <>
                        <div className="modal-overlay" onClick={() => setIsModalVisible(false)} />
                        <AlarmRuleModule 
                            form={form}
                            sensorOptions={sensorOptions}
                            rankOptions={rankOptions}
                            operatorOptions={operatorOptions}
                            onSubmit={(values) => {
                                handleSubmit({
                                    ...values,
                                    log_id: selectedLocation
                                });
                                setIsModalVisible(false);
                            }}
                            onCancel={() => setIsModalVisible(false)}
                        />
                    </>
                )}
            </div>
        </ConfigProvider>
    );
};

// 輔助函數：根據感測器分組規則
const groupRulesBySensor = (rules) => {
    return rules.reduce((groups, rule) => {
        const key = `${rule.log_id}-${rule.sensor_id}`;
        if (!groups[key]) {
            groups[key] = [];
        }
        groups[key].push(rule);
        return groups;
    }, {});
};

// 輔助函數：找到特定等級的規則值
const findRuleValue = (rule, rank) => {
    const field = `threshold_${rank.toLowerCase()}`;
    return rule[field] || '-';
};

export default AlarmSettings; 